"use client";

import { useState } from "react";
import { X, Loader2, ArrowRight, CheckCircle2, ShieldCheck, MessageCircle, Landmark } from "lucide-react";
import { BROKERAGE_PLANS, BRANCHES, rmsForBranch } from "@/lib/demos/stockBrokingData";

interface AccountProposalModalProps {
  isOpen: boolean;
  planId: string;
  onClose: () => void;
}

export function AccountProposalModal({ isOpen, planId, onClose }: AccountProposalModalProps) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [pan, setPan] = useState("");
  const [branch, setBranch] = useState("blr");
  const [plan, setPlan] = useState(planId);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState<{ ref: string; rm: string } | null>(null);

  if (!isOpen) return null;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      const rm = rmsForBranch(branch)[0]?.name ?? "a Relationship Manager";
      setDone({ ref: `JS-BR-${Math.floor(2000 + Math.random() * 7999)}`, rm });
      setSubmitting(false);
    }, 800);
  }

  function reset() {
    setDone(null);
    setName(""); setPhone(""); setEmail(""); setPan("");
    onClose();
  }

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-md overflow-y-auto">
      <div className="relative w-full max-w-md rounded-2xl bg-white border border-slate-200 p-6 sm:p-7 shadow-2xl shadow-emerald-950/40 my-8">
        <button onClick={reset} className="absolute top-5 right-5 text-slate-500 hover:text-slate-900 p-1 rounded-lg hover:bg-slate-100 transition-colors">
          <X className="w-5 h-5" />
        </button>

        {done ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Application received!</h3>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">
              Your account-opening reference is <span className="font-mono text-emerald-600">{done.ref}</span>.
              A WhatsApp KYC link is on its way to <strong>{phone || "your phone"}</strong>.
            </p>
            <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200 text-left space-y-2 text-[11px] text-slate-600">
              <div className="flex items-center gap-2"><MessageCircle className="w-3.5 h-3.5 text-emerald-600" /> WhatsApp KYC link dispatched</div>
              <div className="flex items-center gap-2"><Landmark className="w-3.5 h-3.5 text-emerald-600" /> Routed to branch: {BRANCHES.find((b) => b.id === branch)?.name}</div>
              <div className="flex items-center gap-2"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> RM assigned: {done.rm}</div>
            </div>
            <p className="text-[10px] text-slate-500 mt-3">Open the <strong>Broking CRM</strong> tab to see this lead in the live pipeline.</p>
            <button onClick={reset} className="mt-5 w-full py-2.5 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold text-xs transition-colors">Done</button>
          </div>
        ) : (
          <>
            <div className="mb-5">
              <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-600 border border-emerald-500/20">Open Account</span>
              <h3 className="text-lg font-bold text-slate-900 mt-2">Start your demat account</h3>
              <p className="text-xs text-slate-500 mt-1">Takes 2 minutes. Paperless Aadhaar e-KYC. No opening fee.</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <Field label="Full name (as per PAN)" value={name} onChange={setName} placeholder="Rahul Sharma" required />
              <div className="grid grid-cols-2 gap-3">
                <Field label="Mobile" value={phone} onChange={setPhone} placeholder="+91 98xxx xxxxx" required />
                <Field label="PAN" value={pan} onChange={(v) => setPan(v.toUpperCase())} placeholder="ABCDE1234F" required maxLength={10} />
              </div>
              <Field label="Email" type="email" value={email} onChange={setEmail} placeholder="you@email.com" required />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-semibold text-slate-600 block mb-1">Nearest branch</label>
                  <select value={branch} onChange={(e) => setBranch(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-500">
                    {BRANCHES.map((b) => <option key={b.id} value={b.id}>{b.city}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[11px] font-semibold text-slate-600 block mb-1">Plan</label>
                  <select value={plan} onChange={(e) => setPlan(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-900 text-xs focus:outline-none focus:border-emerald-500">
                    {BROKERAGE_PLANS.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
                  </select>
                </div>
              </div>

              <button
                type="submit"
                disabled={submitting}
                className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-400 hover:to-teal-500 text-white font-bold text-xs shadow-lg shadow-emerald-500/20 flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
              >
                {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting…</> : <>Get KYC link on WhatsApp <ArrowRight className="w-4 h-4" /></>}
              </button>
              <p className="text-center text-[10px] text-slate-500 flex items-center justify-center gap-1">
                <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" /> Demo form — no data is stored or sent anywhere.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = "text", required, maxLength }: {
  label: string; value: string; onChange: (v: string) => void; placeholder?: string; type?: string; required?: boolean; maxLength?: number;
}) {
  return (
    <div>
      <label className="text-[11px] font-semibold text-slate-600 block mb-1">{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required={required}
        maxLength={maxLength}
        className="w-full px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-900 text-xs placeholder:text-slate-500 focus:outline-none focus:border-emerald-500"
      />
    </div>
  );
}
